/**
 * Estilos centralizados para la vista de Tareas
 */

// ==================== COLORES ====================

export const URGENCY_COLORS = {
  vencida: "#EF5350", // Rojo - tarea vencida
  advertencia: "#FFA726", // Ámbar - +14 días transcurridos
  normal: "transparent",
};

export const ESTADO_COLORS = {
  PENDIENTE: { bg: "#FFEBEE", text: "#C62828", dot: "#EF5350" },
  "EN PROCESO": { bg: "#FFF3E0", text: "#E65100", dot: "#FFA726" },
  "OBSERVADA SII": { bg: "#E3F2FD", text: "#1565C0", dot: "#42A5F5" },
  TERMINADA: { bg: "#E8F5E9", text: "#2E7D32", dot: "#66BB6A" },
  "PENDIENTE DE FACTURACION": {
    bg: "#F3E5F5",
    text: "#7B1FA2",
    dot: "#AB47BC",
  },
  FACTURADO: { bg: "#E8F5E9", text: "#2E7D32", dot: "#66BB6A" },
  "CANCELADO O RECHAZADO": { bg: "#FAFAFA", text: "#616161", dot: "#9E9E9E" },
};

// ==================== ESTILOS DE TABLA ====================

export const tableStyles = {
  container: {
    borderRadius: 8,
    border: "1px solid #E0E0E0",
    overflow: "hidden",
  },
  headerCell: {
    backgroundColor: "#FAFAFA",
    fontWeight: 600,
    fontSize: "0.813rem",
    color: "#424242",
    borderBottom: "2px solid #E0E0E0",
    whiteSpace: "nowrap",
  },
  row: {
    height: 60,
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "#FAFAFA",
    },
  },
  cell: {
    fontSize: "0.875rem",
    color: "#212121",
    borderBottom: "1px solid #F5F5F5",
    padding: "12px 16px",
  },
  cellClickable: {
    cursor: "pointer",
  },
};

// ==================== ESTILOS DE FILA CON URGENCIA ====================

/**
 * Genera estilos de fila con barra lateral de urgencia
 * @param {string} diasLimites - Días límites de la tarea
 * @param {number} diasTranscurridos - Días transcurridos
 * @returns {Object} - Estilos para TableRow
 */
export const getRowStyleWithIndicator = (diasLimites, diasTranscurridos) => {
  let borderColor = URGENCY_COLORS.normal;

  if (diasLimites === "0") {
    borderColor = URGENCY_COLORS.vencida;
  } else if (diasLimites === "" && diasTranscurridos >= 14) {
    borderColor = URGENCY_COLORS.advertencia;
  }

  return {
    ...tableStyles.row,
    position: "relative",
    backgroundColor: "#FFFFFF",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: borderColor !== "transparent" ? 4 : 0,
      backgroundColor: borderColor,
    },
    "&:hover": {
      backgroundColor: "#FAFAFA",
    },
  };
};

// ==================== ESTILOS DE CHIPS DE ESTADO ====================

/**
 * Genera estilos para el chip de estado compacto
 * @param {string} estado - Estado de la tarea
 * @returns {Object} - Estilos para el chip
 */
export const getEstadoChipStyles = (estado) => {
  const colors =
    ESTADO_COLORS[estado] || ESTADO_COLORS["CANCELADO O RECHAZADO"];

  return {
    chip: {
      backgroundColor: colors.bg,
      color: colors.text,
      fontWeight: 500,
      fontSize: "0.75rem",
      height: 26,
      cursor: "pointer",
      transition: "all 0.2s ease",
      "&:hover": {
        transform: "scale(1.02)",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      },
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: colors.dot,
      marginRight: 6,
      display: "inline-block",
    },
  };
};

// ==================== ESTILOS DE BOTONES ====================

export const buttonStyles = {
  export: {
    borderColor: "#BDBDBD",
    color: "#616161",
    fontWeight: 500,
    textTransform: "none",
    "&:hover": {
      borderColor: "#9E9E9E",
      backgroundColor: "#FAFAFA",
    },
  },
  delete: {
    color: "#9E9E9E",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#EF5350",
      backgroundColor: "#FFEBEE",
    },
  },
  subtareas: {
    color: "#1976D2",
    fontSize: "0.813rem",
    fontWeight: 500,
    textTransform: "none",
    padding: "4px 8px",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: "#E3F2FD",
    },
  },
};

// ==================== ESTILOS DE ICONOS ====================

export const iconStyles = {
  attachment: {
    color: "#9E9E9E",
    fontSize: 20,
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#1976D2",
    },
  },
};

// ==================== ESTILOS DE CARD/CONTENEDOR ====================

export const cardStyles = {
  root: {
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #E0E0E0",
    overflow: "hidden",
  },
  header: {
    padding: "16px 20px",
    borderBottom: "1px solid #F5F5F5",
    backgroundColor: "#FFFFFF",
  },
};

// ==================== BADGE DÍAS TRANSCURRIDOS ====================

/**
 * Genera estilos para el badge de días transcurridos
 * @param {number} dias - Número de días
 * @returns {Object} - Estilos para el badge
 */
export const getDiasTranscurridosBadgeStyle = (dias) => {
  let bgColor = "#E8F5E9"; // Verde claro - normal
  let textColor = "#2E7D32";

  if (dias >= 30) {
    bgColor = "#FFEBEE"; // Rojo claro
    textColor = "#C62828";
  } else if (dias >= 14) {
    bgColor = "#FFF3E0"; // Ámbar claro
    textColor = "#E65100";
  } else if (dias >= 7) {
    bgColor = "#FFFDE7"; // Amarillo claro
    textColor = "#F57F17";
  }

  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 12,
    backgroundColor: bgColor,
    color: textColor,
    fontSize: "0.813rem",
    fontWeight: 500,
  };
};
